/**
 * MapControlsBar — the map's zoom + view controls as a THIN, persistent bar
 * beneath the map (full-width, paper fill, olive border-y, ~40 px tall) rather
 * than chips floating over the board.
 *
 * Two connected icon groups, centred (design v3, 2026-11-03):
 *   - zoom: [⛶ reset-to-fit | − | +], acting directly on the current scale
 *     while pan/pinch stay live;
 *   - view: [Game | Regions] (a mode: the selected one fills blood), then
 *     Players in live games and ★ on maps with a rules overlay, always last.
 *     Players and ★ are momentary: the segment fills olive (Players, while
 *     its filter bar is open) or amber (★, while its overlay is open). The
 *     group is one segmented control: only one segment is active, so while
 *     either is open neither mode segment is selected (lib/mapViewGroup.ts
 *     owns the tap rules).
 *
 * The players filter bar (`belowBar`) hangs directly under the bar, inside
 * the same slot, so it tracks the bar's width.
 *
 * Segment width = icon + 2 × padX: padX 10 on compact (phone) bars, 8 when a
 * 4-segment view group sits on a bar under 375 px, 14 otherwise. hitSlop
 * makes every segment a 44 px tall target.
 *
 * Purely presentational: the parent owns zoom scale / pan, the current mode
 * and the overlays, and passes handlers down.
 */
import React from 'react'
import { View, Text, Pressable } from 'react-native'
import { MaterialCommunityIcons } from '@expo/vector-icons'
import { useMapDimensions } from '../../lib/useMapDimensions'
import { GAME_CONTROL_EDGE_INSET } from '../../lib/gameChrome'
import type { GameState } from '../../lib/gameState'
import { useTheme } from '../../hooks/useTheme'
import { overlayPalette } from '../../lib/rulesOverlayTheme'
import { showFocusRing } from '../../lib/keyboardFocus'

export type { MapViewMode } from '../../lib/mapViewGroup'
import type { MapViewMode } from '../../lib/mapViewGroup'

/** MaterialCommunityIcons for the view group (design v3, Set A). */
export const MAP_BAR_ICONS = {
  game: 'sword-cross',
  regions: 'map-search-outline',
  players: 'account-group',
  rules: 'star',
} as const

/** The bar width under which a 4-segment view group tightens to padX 8. */
export const MAP_BAR_NARROW_WIDTH = 375

/**
 * Sizes of the bar's segments. `segments` is the view group's count (2–4);
 * `narrow` is a bar under {@link MAP_BAR_NARROW_WIDTH}.
 */
export function mapBarMetrics({ compact, segments, narrow }: { compact: boolean; segments: number; narrow: boolean }) {
  const size: 26 | 30 = compact ? 26 : 30
  const iconSize = compact ? 16 : 18
  const padX = !compact ? 14 : segments >= 4 && narrow ? 8 : 10
  return { size, iconSize, padX, segmentWidth: iconSize + 2 * padX }
}

/**
 * Wraps the control bar so, in LANDSCAPE, it's exactly as wide as the rendered
 * map (svgWidth) and aligned the same way — instead of stretching across the
 * whole right pane past the letterboxed map's edge. Portrait uses the full
 * width and aligns accessories with the inline chat/log controls. reservedBottom:44 matches the
 * landscape MapView reserves for this bar, so svgWidth matches the map exactly.
 *
 * `fullWidth` opts out of the svgWidth constraint for screens where the map
 * column IS the map area (the /log scrubber + the in-game snapshot view): there
 * the map sits in a header-reduced, measured container that the window-based
 * useMapDimensions can't model, so the bar should simply span the full column
 * beneath the map rather than track a mis-computed svgWidth (which left a dark
 * gutter). The game screen keeps the default alignment (map is letterboxed in a
 * wider pane with a roster gutter, so tracking svgWidth is correct there).
 */
export function MapControlsBarSlot({
  state,
  children,
  fullWidth = false,
}: {
  state: GameState
  children: React.ReactNode
  fullWidth?: boolean
}) {
  const { isLandscape, containerWidth, svgWidth } = useMapDimensions(state, { reservedBottom: 44 })
  const barWidth = !isLandscape || fullWidth ? containerWidth : svgWidth
  const aligned = React.isValidElement(children)
    ? React.cloneElement(children as React.ReactElement<{ portraitActionAlignment?: boolean; compact?: boolean; narrow?: boolean }>, {
      portraitActionAlignment: !isLandscape,
      narrow: barWidth < MAP_BAR_NARROW_WIDTH,
      ...(!isLandscape && containerWidth < 360 ? { compact: true } : {}),
    }) : children
  if (!isLandscape || fullWidth) return <>{aligned}</>
  // The MapView's container is left-aligned in the map pane (its width is
  // containerWidth, which already subtracts the reservedRight gutter), and the
  // SVG is centred inside it with `sidePad` on each side. Match that exactly:
  // left-align the bar and offset it by sidePad + constrain to svgWidth, so it
  // tracks the map's left/right edges instead of the full pane.
  const sidePad = Math.max(0, (containerWidth - svgWidth) / 2)
  // Shrink the controls when the map (and thus the bar) is narrow.
  const childProps = React.isValidElement(children)
    ? children.props as { compact?: boolean; leftAccessory?: unknown; rightAccessory?: unknown }
    : null
  const hasAccessory = !!childProps?.leftAccessory || !!childProps?.rightAccessory
  const compactAt = hasAccessory ? 420 : 320
  const compact = svgWidth < compactAt
  const bar = React.isValidElement(aligned)
    ? React.cloneElement(aligned as React.ReactElement<{ compact?: boolean }>, {
      compact: compact || !!childProps?.compact,
    })
    : aligned
  return (
    <View style={{ alignSelf: 'flex-start', marginLeft: sidePad, width: svgWidth }}>
      {bar}
    </View>
  )
}

export interface MapControlsBarProps {
  /** Reset the zoom + pan back to the default framing. */
  onReset: () => void
  /** Step the zoom in / out by one increment. */
  onZoomIn: () => void
  onZoomOut: () => void
  /** Bound flags — the matching stepper greys out at the min/max scale. */
  canZoomIn: boolean
  canZoomOut: boolean
  /** Current view mode + change handler for the view group. Omit
   * onModeChange (e.g. the regions-only map viewer) to hide the view group
   * and show just the centred zoom group. */
  mode?: MapViewMode
  onModeChange?: (mode: MapViewMode) => void
  /** Players segment: the players (or teams) filter bar under the map bar. */
  onTogglePlayers?: () => void
  playersOpen?: boolean
  /** Team games: the segment filters by team. */
  playersByTeam?: boolean
  /** ★ segment: only on maps with a rules overlay. Toggles the overlay. Always last. */
  onToggleRules?: () => void
  rulesOpen?: boolean
  /** Receives the ★ segment's view (the first-entry coach mark points at it). */
  rulesAnchorRef?: React.Ref<View>
  /** Drawn directly under the bar: the players filter bar while it is open. */
  belowBar?: React.ReactNode
  /** Shrink the controls to fit a narrow bar (set by MapControlsBarSlot in
   * landscape when the map's rendered width is small). */
  compact?: boolean
  /** The bar is under {@link MAP_BAR_NARROW_WIDTH} (set by MapControlsBarSlot). */
  narrow?: boolean
  /** Supplied by the keyboard-stable map slot, never inferred from keyboard height. */
  portraitActionAlignment?: boolean
  /** Independent leading action; mirrors rightAccessory. */
  leftAccessory?: React.ReactNode | ((controlSize: 26 | 30) => React.ReactNode)
  /** Independent trailing action; the main control group stays centred. */
  rightAccessory?: React.ReactNode | ((controlSize: 26 | 30) => React.ReactNode)
  /** Bottom rule colour override — portrait passes a lighter one when the
   *  chat input sits directly beneath, so the two read as one block. */
  bottomRuleColor?: string
}

/** One segment of a connected group. */
export interface BarSegment {
  key: string
  /** A MaterialCommunityIcons name, or a text glyph (zoom). */
  icon?: React.ComponentProps<typeof MaterialCommunityIcons>['name']
  glyph?: string
  accessibilityLabel: string
  accessibilityHint?: string
  testID?: string
  onPress: () => void
  disabled?: boolean
  /** A mode segment that is the current mode (blood fill). */
  selected?: boolean
  /** A momentary segment that is open: amber (★ rules) or olive (players filter) fill. */
  open?: 'rules' | 'players'
  /** Icon colour when not selected or open (★ is amber). */
  tint?: string
  anchorRef?: React.Ref<View>
}

const FOCUS_RING = '#2b7fff'
const HIT_SLOP = { top: 9, bottom: 9 }

/**
 * A connected group of icon segments: olive border and dividers, paper fill.
 * Pressed segments darken (paper-up plus 8% ink); disabled ones fade to 40%.
 */
export function BarSegmentGroup({
  segments,
  size,
  width,
  iconSize,
  testID,
  accessibilityRole,
  accessibilityLabel,
}: {
  segments: readonly BarSegment[]
  size: number
  width: number
  iconSize: number
  testID?: string
  accessibilityRole?: 'radiogroup'
  accessibilityLabel?: string
}) {
  const { theme } = useTheme()
  const palette = overlayPalette(theme)
  return (
    <View
      testID={testID}
      accessibilityRole={accessibilityRole}
      accessibilityLabel={accessibilityLabel}
      className="flex-row border border-olive rounded-md"
      style={{ overflow: 'hidden' }}
    >
      {segments.map((segment, index) => {
        const filled = segment.selected ? palette.blood : segment.open === 'rules' ? palette.amber : segment.open === 'players' ? palette.olive : null
        const color = filled ? '#ffffff' : segment.tint ?? palette.ink
        return (
          <React.Fragment key={segment.key}>
            {index > 0 ? <View style={{ width: 1 }} className="bg-olive" /> : null}
            <Pressable
              ref={segment.anchorRef}
              onPress={segment.onPress}
              disabled={segment.disabled}
              hitSlop={HIT_SLOP}
              accessibilityRole="button"
              accessibilityLabel={segment.accessibilityLabel}
              accessibilityHint={segment.accessibilityHint}
              accessibilityState={{
                disabled: !!segment.disabled,
                ...(segment.selected !== undefined ? { selected: segment.selected } : {}),
                // The momentary segments (Players, ★) report whether they are open.
                ...(segment.key === 'rules' || segment.key === 'players' ? { expanded: !!segment.open } : {}),
              }}
              // Web: RN web drops accessibilityState on buttons; ARIA props reach the DOM.
              {...(segment.key === 'rules' || segment.key === 'players' ? { 'aria-expanded': !!segment.open } : {})}
              {...(segment.selected !== undefined ? { 'aria-pressed': segment.selected } : {})}
              testID={segment.testID}
              style={(state) => {
                const { pressed, focused } = state as { pressed: boolean; focused?: boolean }
                return {
                  width,
                  height: size,
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: filled ?? (pressed ? palette.paperUp : palette.paper),
                  opacity: segment.disabled ? 0.4 : 1,
                  ...(showFocusRing(focused) ? { outlineColor: FOCUS_RING, outlineWidth: 2, outlineStyle: 'solid', outlineOffset: -3 } : {}),
                }
              }}
            >
              {({ pressed }) => (
                <>
                  {pressed ? (
                    <View
                      pointerEvents="none"
                      style={{ position: 'absolute', left: 0, right: 0, top: 0, bottom: 0, backgroundColor: `rgba(0,0,0,${filled ? 0.18 : 0.08})` }}
                    />
                  ) : null}
                  {segment.icon ? (
                    <MaterialCommunityIcons name={segment.icon} size={iconSize} color={color} />
                  ) : (
                    <Text
                      style={{ color, fontSize: Math.round(size * 0.56), lineHeight: size, marginTop: -1, textAlign: 'center' }}
                    >
                      {segment.glyph}
                    </Text>
                  )}
                </>
              )}
            </Pressable>
          </React.Fragment>
        )
      })}
    </View>
  )
}

/** The view group's segments, in order: Game, Regions, then Players and ★ (always last) when offered. */
export function mapViewSegments({
  mode,
  onModeChange,
  onTogglePlayers,
  playersOpen,
  playersByTeam,
  onToggleRules,
  rulesOpen,
  rulesAnchorRef,
  amber,
}: Pick<MapControlsBarProps, 'mode' | 'onModeChange' | 'onTogglePlayers' | 'playersOpen' | 'playersByTeam' | 'onToggleRules' | 'rulesOpen' | 'rulesAnchorRef'> & { amber: string }): BarSegment[] {
  if (!onModeChange) return []
  // One active segment: an open filter or overlay outranks the mode underneath it.
  const overlayOpen = (!!onToggleRules && !!rulesOpen) || (!!onTogglePlayers && !!playersOpen)
  const out: BarSegment[] = [
    {
      key: 'game', icon: MAP_BAR_ICONS.game, accessibilityLabel: 'Game view', accessibilityHint: 'Shows armies and owners',
      testID: 'map-view-game', selected: !overlayOpen && mode !== 'regions', onPress: () => onModeChange('game'),
    },
    {
      key: 'regions', icon: MAP_BAR_ICONS.regions, accessibilityLabel: 'Regions view', accessibilityHint: 'Shows region bonuses',
      testID: 'map-view-regions', selected: !overlayOpen && mode === 'regions', onPress: () => onModeChange('regions'),
    },
  ]
  if (onTogglePlayers) {
    out.push({
      key: 'players', icon: MAP_BAR_ICONS.players,
      accessibilityLabel: playersByTeam ? 'Filter the map by team' : 'Filter the map by player',
      accessibilityHint: playersByTeam ? "Brings chosen teams' territories forward" : "Brings chosen players' territories forward",
      testID: 'map-players-button', open: playersOpen ? 'players' : undefined, onPress: onTogglePlayers,
    })
  }
  if (onToggleRules) {
    out.push({
      key: 'rules', icon: MAP_BAR_ICONS.rules, accessibilityLabel: 'Special rules for this map',
      accessibilityHint: "Opens the map's special rules", testID: 'map-rules-button', tint: amber,
      open: rulesOpen ? 'rules' : undefined, onPress: onToggleRules, anchorRef: rulesAnchorRef,
    })
  }
  return out
}

export function MapControlsBar({
  onReset,
  onZoomIn,
  onZoomOut,
  canZoomIn,
  canZoomOut,
  mode,
  onModeChange,
  onTogglePlayers,
  playersOpen = false,
  playersByTeam = false,
  onToggleRules,
  rulesOpen = false,
  rulesAnchorRef,
  belowBar,
  compact = false,
  narrow = false,
  portraitActionAlignment = false,
  leftAccessory,
  rightAccessory,
  bottomRuleColor,
}: MapControlsBarProps) {
  const { theme } = useTheme()
  const view = mapViewSegments({
    mode, onModeChange, onTogglePlayers, playersOpen, playersByTeam, onToggleRules, rulesOpen, rulesAnchorRef,
    amber: overlayPalette(theme).amber,
  })
  // Responsive sizing — `compact` is set by MapControlsBarSlot when the map's
  // rendered width is narrow, so the controls shrink to stay on one row
  // instead of clipping (without flipping to the portrait layout).
  const { size, iconSize, segmentWidth } = mapBarMetrics({ compact, segments: view.length, narrow })
  const tight = portraitActionAlignment && !!(leftAccessory || rightAccessory)
  const setGap = compact || tight ? 8 : 24
  const renderAccessory = (accessory: MapControlsBarProps['leftAccessory']) =>
    typeof accessory === 'function' ? accessory(size) : accessory
  const zoom: BarSegment[] = [
    { key: 'reset', glyph: '⛶', accessibilityLabel: 'Reset zoom to fit', testID: 'map-zoom-reset', onPress: onReset },
    { key: 'out', glyph: '−', accessibilityLabel: 'Zoom out', testID: 'map-zoom-out', disabled: !canZoomOut, onPress: onZoomOut },
    { key: 'in', glyph: '+', accessibilityLabel: 'Zoom in', testID: 'map-zoom-in', disabled: !canZoomIn, onPress: onZoomIn },
  ]

  const bar = (
    <View
      accessibilityLabel="Map controls"
      // Centred group: the zoom group, then the view group (no divider —
      // saves width). Spacing/sizing shrink in `compact` mode.
      className={`${compact ? 'px-2' : 'px-3'} py-1 bg-paper border-y border-olive flex-row items-center justify-center`}
      style={{
        minHeight: leftAccessory || rightAccessory ? 44 : 40,
        gap: setGap,
        ...(bottomRuleColor ? { borderBottomColor: bottomRuleColor } : null),
      }}
    >
      {/* Independent edge actions stay outside the centered zoom/view group. */}
      {leftAccessory ? (
        // Match the leading ALL / CHAT control in the revealed chat/log bar.
        <View style={{ position: 'absolute', left: GAME_CONTROL_EDGE_INSET, top: 0, bottom: 0, justifyContent: 'center' }}>
          {renderAccessory(leftAccessory)}
        </View>
      ) : null}
      <BarSegmentGroup testID="map-zoom-group" segments={zoom} size={size} width={segmentWidth} iconSize={iconSize} />
      {view.length > 0 ? (
        <BarSegmentGroup
          testID="map-view-group"
          accessibilityRole="radiogroup"
          accessibilityLabel="Map view"
          segments={view}
          size={size}
          width={segmentWidth}
          iconSize={iconSize}
        />
      ) : null}
      {rightAccessory ? (
        // Match the trailing grow control in the revealed chat/log bar.
        <View style={{ position: 'absolute', right: GAME_CONTROL_EDGE_INSET, top: 0, bottom: 0, justifyContent: 'center' }}>
          {renderAccessory(rightAccessory)}
        </View>
      ) : null}
    </View>
  )
  return belowBar ? <>{bar}{belowBar}</> : bar
}
